"use client";

import { config } from "@/shared/lib/config";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookupForInfinite } from "@/shared/lib/utils";
import { InfiniteMultiCombobox } from "@/shared/ui/InfiniteMultiCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useState } from "react";
import type { SalesSummaryParams } from "../../api/client";

type SalesReportsFilterProps = {
  filters: SalesSummaryParams;
  onFiltersChange: (filters: SalesSummaryParams) => void;
};

type BranchLookupItem = {
  id: string;
  name: string;
};

export function SalesReportsFilter({
  filters,
  onFiltersChange,
}: SalesReportsFilterProps) {
  const [fromDate, setFromDate] = useState<string>(
    filters.fromDate ? formatDateForInput(filters.fromDate) : "",
  );
  const [toDate, setToDate] = useState<string>(
    filters.toDate ? formatDateForInput(filters.toDate) : "",
  );
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>(
    filters.branchIds ?? [],
  );
  const [channel, setChannel] = useState<"APP" | "WALKIN" | undefined>(
    filters.channel,
  );
  const [orderStatus, setOrderStatus] = useState<
    "all" | "completed" | "pending"
  >(filters.statusFilter ?? "completed");

  const handleDateRangeChange = (
    range:
      | {
          from?: Date;
          to?: Date;
        }
      | Date[],
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;

    const fromStr = from ? formatDateForInput(from) : "";
    const toStr = to ? formatDateForInput(to) : "";

    setFromDate(fromStr);
    setToDate(toStr);

    const fromDateObj = from
      ? new Date(new Date(from).setHours(0, 0, 0, 0))
      : undefined;
    const toDateObj = to
      ? new Date(new Date(to).setHours(23, 59, 59, 999))
      : undefined;

    onFiltersChange({
      ...filters,
      fromDate: fromDateObj,
      toDate: toDateObj,
    });
  };

  const handleBranchChange = (branchIds: string[]) => {
    setSelectedBranchIds(branchIds);
    onFiltersChange({
      ...filters,
      branchIds: branchIds.length > 0 ? branchIds : undefined,
    });
  };

  const handleChannelChange = (value: string) => {
    const newChannel =
      value === "all" ? undefined : (value as "APP" | "WALKIN");
    setChannel(newChannel);
    onFiltersChange({
      ...filters,
      channel: newChannel,
    });
  };

  const handleOrderStatusChange = (value: string) => {
    const newStatus = value as "all" | "completed" | "pending";
    setOrderStatus(newStatus);
    onFiltersChange({
      ...filters,
      statusFilter: newStatus,
    });
  };

  const handleReset = () => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);

    setFromDate(formatDateForInput(fromDate));
    setToDate(formatDateForInput(toDate));
    setSelectedBranchIds([]);
    setChannel(undefined);
    setOrderStatus("completed");

    onFiltersChange({
      fromDate,
      toDate,
      statusFilter: "completed",
    });
  };

  const hasFilters =
    fromDate ||
    toDate ||
    selectedBranchIds.length > 0 ||
    channel ||
    orderStatus !== "completed";

  // Branches lookup function for InfiniteMultiCombobox
  const branchesLookupFn = async (args: {
    search: string;
    pageParam: number;
  }) => {
    return fetchLookupForInfinite<BranchLookupItem>(
      `${config.apiUrl}/branches/lookup`,
      args,
    );
  };

  const preloadBranchesFn = async (
    ids: string[],
  ): Promise<BranchLookupItem[]> => {
    if (ids.length === 0) return [];
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          const data = await fetcher.get<{ item: BranchLookupItem | null }>(
            `${config.apiUrl}/branches/lookup/${encodeURIComponent(id)}`,
          );
          return data.item;
        } catch {
          return null;
        }
      }),
    );
    return results.filter((item): item is BranchLookupItem => item !== null);
  };

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-xl border bg-card p-4">
      {/* Order Status Tabs */}
      <div className="flex items-center gap-2">
        <p className="text-muted-foreground text-sm">ສະຖານະອໍເດີ:</p>
        <Tabs value={orderStatus} onValueChange={handleOrderStatusChange}>
          <TabsList>
            <TabsTrigger value="all">ທັງໝົດ</TabsTrigger>
            <TabsTrigger value="completed">ສຳເລັດແລ້ວ</TabsTrigger>
            <TabsTrigger value="pending">ລໍຖ້າ</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Range */}
        <DatePicker
          mode="range"
          value={{
            from: fromDate ? parseISO(fromDate) : undefined,
            to: toDate ? parseISO(toDate) : undefined,
          }}
          onChange={(date) => {
            if (date) {
              handleDateRangeChange(date);
            }
          }}
          placeholder="ຊ່ວງວັນທີ"
          className="h-9 w-full sm:w-fit"
        />

        {/* Branch Multi-Select */}
        <InfiniteMultiCombobox<BranchLookupItem>
          queryKey={["branches", "lookup"]}
          queryFn={branchesLookupFn}
          preloadQueryFn={preloadBranchesFn}
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          values={selectedBranchIds}
          onValuesChange={handleBranchChange}
          placeholder="ເລືອກສາຂາ..."
          className="h-9 w-full sm:w-64"
          clearable
        />

        {/* Channel Select */}
        <SimpleSelect
          placeholder="ຊ່ອງທາງ"
          value={channel || "all"}
          onValueChange={handleChannelChange}
          className="h-9 w-full sm:w-40"
          options={[
            { value: "all", label: "ຊ່ອງທາງທັງໝົດ" },
            { value: "APP", label: "ແອັບ" },
            { value: "WALKIN", label: "ມາຮ້ານ" },
          ]}
        />

        {/* Reset Button */}
        {hasFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="h-9"
          >
            ລ້າງການກອງ
          </Button>
        )}
      </div>
    </div>
  );
}
